// hooks/useLocations.tsx
import { useState, useEffect, useContext, createContext, ReactNode } from 'react';

// Import your existing location config
import { LOCATION_OPTIONS, LocationConfig } from '../config/locationConfig';

interface LocationsContextType {
  locations: LocationConfig[];
  loading: boolean;
  error: string | null;
  
  // Helper functions matching your existing structure
  getLocationById: (locationId: string) => LocationConfig;
  getLocationByRegion: (region: string) => LocationConfig;
  getLocationByOrganization: (organizationName: string) => LocationConfig;
  
  // New organization-focused helper functions
  getOrganizationName: (organizationId: string) => string;
  getOrganizationById: (organizationId: string) => LocationConfig | null;
  getLocationsByOrganization: (organizationId: string) => LocationConfig[];
  getOrganizations: () => Array<{ id: string; name: string; locationName: string }>;
  getAllOrganizationIds: () => string[];
}

// Create context
const LocationsContext = createContext<LocationsContextType | null>(null);

// Provider component
interface LocationsProviderProps {
  children: ReactNode;
}

export const LocationsProvider: React.FC<LocationsProviderProps> = ({ children }) => {
  const [locations, setLocations] = useState<LocationConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Function to load locations (from static config for now)
  const loadLocations = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // For now, just use the static data
      // Later this can be replaced with an API call
      await new Promise(resolve => setTimeout(resolve, 100)); // Simulate loading
      setLocations(LOCATION_OPTIONS);
      
    } catch (err) {
      console.error('Error loading locations:', err);
      setError('Failed to load location data');
      setLocations(LOCATION_OPTIONS); // Fallback to static data
    } finally {
      setLoading(false);
    }
  };

  // Helper functions - maintaining your existing structure
  const getLocationById = (locationId: string): LocationConfig => {
    return locations.find(loc => loc.id === locationId) || locations[0];
  };

  const getLocationByRegion = (region: string): LocationConfig => {
    return locations.find(loc => loc.appRegion === region) || locations[0];
  };

  const getLocationByOrganization = (organizationName: string): LocationConfig => {
    return locations.find(loc => loc.organizationName === organizationName) || locations[0];
  };

  // NEW: Organization-focused functions for better UX
  const getOrganizationName = (organizationId: string): string => {
    const location = locations.find(loc => loc.organizationId === organizationId);
    return location?.organizationName || organizationId; // Fallback to ID if not found
  };

  const getOrganizationById = (organizationId: string): LocationConfig | null => {
    return locations.find(loc => loc.organizationId === organizationId) || null;
  };

  const getLocationsByOrganization = (organizationId: string): LocationConfig[] => {
    return locations.filter(loc => loc.organizationId === organizationId);
  };

  const getOrganizations = (): Array<{ id: string; name: string; locationName: string }> => {
    // Remove duplicates in case there are multiple locations per organization
    const organizations = new Map<string, { id: string; name: string; locationName: string }>();
    
    locations.forEach(loc => {
      if (!organizations.has(loc.organizationId)) {
        organizations.set(loc.organizationId, {
          id: loc.organizationId,
          name: loc.organizationName,
          locationName: loc.locationName
        });
      }
    });

    return Array.from(organizations.values());
  };

  const getAllOrganizationIds = (): string[] => {
    const orgIds = new Set<string>();
    locations.forEach(loc => orgIds.add(loc.organizationId));
    return Array.from(orgIds);
  };

  // Load locations on mount
  useEffect(() => {
    loadLocations();
  }, []);

  const contextValue: LocationsContextType = {
    locations,
    loading,
    error,
    
    // Your existing helper functions
    getLocationById,
    getLocationByRegion,
    getLocationByOrganization,
    
    // New organization-focused functions
    getOrganizationName,
    getOrganizationById,
    getLocationsByOrganization,
    getOrganizations,
    getAllOrganizationIds
  };

  return (
    <LocationsContext.Provider value={contextValue}>
      {children}
    </LocationsContext.Provider>
  );
};

// Hook to use locations
export const useLocations = (): LocationsContextType => {
  const context = useContext(LocationsContext);
  if (!context) {
    throw new Error('useLocations must be used within a LocationsProvider');
  }
  return context;
};

export default useLocations;